import type mapboxgl from 'mapbox-gl'

// Rounded-square marker image loader, extracted from Map.vue. Each Mapbox
// instance gets its OWN loader (via createMapImageLoader) so a cached image is
// only short-circuited for the map that already has it — sharing one cache
// across instances would leave later maps without the addImage() call.

const MAX_IMAGE_CACHE_SIZE = 100
const IMAGE_SIZE = 100        // Canvas size (larger for crisp rendering)
const DISPLAY_SIZE = 50       // Desired display size on map
const PIXEL_RATIO = IMAGE_SIZE / DISPLAY_SIZE
const BORDER_WIDTH = 4
const CORNER_RADIUS = 8

export interface MapImageLoader {
  loadImageAndCache: (map: mapboxgl.Map, code: string, url: string) => Promise<void>
  clear: () => void
}

export function createMapImageLoader(): MapImageLoader {
  const imageCache = new Map<string, Promise<void>>()

  const loadImageAndCache = (map: mapboxgl.Map, code: string, url: string): Promise<void> => {
    if (imageCache.has(code)) {
      const value = imageCache.get(code)!
      imageCache.delete(code)
      imageCache.set(code, value)
      return value
    }

    if (imageCache.size >= MAX_IMAGE_CACHE_SIZE) {
      const oldestKey = imageCache.keys().next().value
      if (oldestKey !== undefined) imageCache.delete(oldestKey)
    }

    const loadingPromise = new Promise<void>((resolve) => {
      map.loadImage(url, (error: any, image: any) => {
        if (error) {
          // Evict so a later styleimagemissing can retry after a transient failure.
          imageCache.delete(code)
          resolve()
          return
        }

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')!

        canvas.width = IMAGE_SIZE
        canvas.height = IMAGE_SIZE

        ctx.clearRect(0, 0, IMAGE_SIZE, IMAGE_SIZE)

        ctx.fillStyle = 'white'
        ctx.beginPath()
        ctx.roundRect(0, 0, IMAGE_SIZE, IMAGE_SIZE, CORNER_RADIUS)
        ctx.fill()

        const innerSize = IMAGE_SIZE - BORDER_WIDTH * 2
        const innerRadius = Math.max(0, CORNER_RADIUS - BORDER_WIDTH / 2)

        ctx.save()
        ctx.beginPath()
        ctx.roundRect(BORDER_WIDTH, BORDER_WIDTH, innerSize, innerSize, innerRadius)
        ctx.clip()

        ctx.drawImage(image, BORDER_WIDTH, BORDER_WIDTH, innerSize, innerSize)
        ctx.restore()

        const resizedImage = ctx.getImageData(0, 0, IMAGE_SIZE, IMAGE_SIZE)

        if (!map.hasImage(code)) {
          map.addImage(code, resizedImage as any, { pixelRatio: PIXEL_RATIO })
        }

        resolve()
      })
    })

    imageCache.set(code, loadingPromise)
    return loadingPromise
  }

  return { loadImageAndCache, clear: () => imageCache.clear() }
}
